import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const TRIGGER_LABELS = {
  payment_received: 'Al recibir pago',
  payment_sent: 'Al enviar pago',
  task_completed: 'Al completar tarea',
  balance_threshold: 'Umbral de balance',
  marketplace_sale: 'Al vender en marketplace',
  escrow_event: 'Evento de escrow',
  schedule: 'Programado',
  keyword: 'Palabra clave en mensaje',
  ride_completed: 'Viaje completado',
  rating_changed: 'Rating cambia',
  smart_contract_fulfilled: 'Smart Contract cumplido',
  payout_received: 'Payout recibido',
  domain_item_created: 'Item de dominio creado',
  kyc_level_changed: 'Nivel KYC cambia',
  low_balance: 'Balance bajo'
};

const ACTION_LABELS = {
  notify: 'Notificar',
  auto_reply: 'Respuesta automatica',
  transfer: 'Enviar CUALcoin',
  save_percentage: 'Guardar % en ahorro',
  tag_transaction: 'Etiquetar transaccion',
  alert_jarvis: 'Alertar a JAVIER',
  log_entry: 'Crear entrada en bitacora',
  create_domain_item: 'Crear item en dominio',
  send_message: 'Enviar mensaje',
  request_payout: 'Solicitar payout',
  block_user: 'Bloquear usuario',
  export_report: 'Generar reporte'
};

const EXAMPLES = [
  'Cuando reciba un pago mayor a 100 CUALcoin, notificarme con "Pago grande recibido"',
  'Cada vez que complete una tarea, guardar el 10% de la recompensa en ahorro',
  'Si mi balance baja de 50 CUALcoin, alertar a JAVIER',
  'Cuando venda algo en el marketplace, crear una entrada en mi bitacora',
  'Cuando se complete un viaje como conductor, enviar un mensaje al pasajero',
  'Si mi rating baja, notificarme inmediatamente',
  'Cuando se cumpla una condicion de Smart Contract, generar un reporte',
  'Si mi balance baja de 20, solicitar payout automatico de todo mi pendiente'
];

const AutomationsComponent = ({ user }) => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [history, setHistory] = useState({});
  const [showReference, setShowReference] = useState(false);
  const [triggersMeta, setTriggersMeta] = useState(null);
  const [testTrigger, setTestTrigger] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => { fetchAutomations(); fetchTriggersMeta(); }, []);

  const fetchAutomations = async () => {
    try {
      const res = await axios.get(`${API}/automations/`);
      setAutomations(res.data.automations || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchTriggersMeta = async () => {
    try {
      const res = await axios.get(`${API}/automations/triggers`);
      setTriggersMeta(res.data);
    } catch (err) { console.error(err); }
  };

  const testTriggerFire = async (triggerType) => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/automations/test-trigger`, {
        trigger_type: triggerType, trigger_data: { amount: 100, balance: 10, role: "driver", fare: 50 }
      });
      alert(res.data.message);
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setTesting(false); }
  };

  const createAutomation = async () => {
    if (!description.trim()) return;
    setCreating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/automations/create`, { description });
      setResult(res.data);
      if (res.data.status === 'created') {
        setDescription('');
        fetchAutomations();
      }
    } catch (err) { setResult({ status: 'error', message: err.response?.data?.detail || 'Error' }); }
    finally { setCreating(false); }
  };

  const toggleAutomation = async (id) => {
    try {
      await axios.post(`${API}/automations/${id}/toggle`);
      fetchAutomations();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const deleteAutomation = async (id) => {
    if (!window.confirm('Eliminar esta automatizacion?')) return;
    try {
      await axios.delete(`${API}/automations/${id}`);
      fetchAutomations();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const fetchHistory = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    try {
      const res = await axios.get(`${API}/automations/${id}/history`);
      setHistory(prev => ({ ...prev, [id]: res.data.history || [] }));
      setExpandedId(id);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="automations-module">
      {/* Creator */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Nodo Programativo IA</h2>
          <p className="text-sm text-gray-500 mt-0.5">Describe en lenguaje natural lo que quieres automatizar</p>
        </div>
        <div className="p-6 space-y-4">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
            data-testid="automation-input" rows={3}
            placeholder="Ej: Cuando reciba un pago mayor a 100 CUALcoin, notificarme..."
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />

          <div className="flex flex-wrap gap-1.5">
            {EXAMPLES.map((ex, i) => (
              <button key={i} onClick={() => setDescription(ex)}
                className="text-xs px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition-colors truncate max-w-xs">
                {ex.slice(0, 50)}...
              </button>
            ))}
          </div>

          <button onClick={createAutomation} disabled={creating || !description.trim()}
            data-testid="create-automation-btn"
            className="w-full bg-indigo-600 text-white py-2.5 rounded-lg hover:bg-indigo-500 disabled:opacity-50 font-medium transition-colors">
            {creating ? 'Interpretando...' : 'Crear Automatizacion'}
          </button>

          {/* Reference toggle */}
          <div className="flex items-center gap-2">
            <button onClick={() => setShowReference(!showReference)} data-testid="toggle-reference-btn"
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">
              {showReference ? 'Ocultar referencia' : 'Ver triggers y acciones disponibles'}
            </button>
          </div>

          {showReference && triggersMeta && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="triggers-reference">
              <div>
                <p className="text-xs font-bold text-gray-700 mb-2">Triggers ({Object.keys(triggersMeta.triggers).length})</p>
                <div className="space-y-1">
                  {Object.entries(triggersMeta.triggers).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between bg-purple-50 rounded-lg px-2.5 py-1.5">
                      <div>
                        <span className="text-xs font-medium text-purple-700">{val.label}</span>
                        {val.params.length > 0 && <span className="text-[10px] text-purple-400 ml-1">({val.params.join(', ')})</span>}
                      </div>
                      <button onClick={() => testTriggerFire(key)} disabled={testing} data-testid={`test-trigger-${key}`}
                        className="text-[10px] px-1.5 py-0.5 bg-purple-200 text-purple-700 rounded hover:bg-purple-300 disabled:opacity-50">
                        Test
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-700 mb-2">Acciones ({Object.keys(triggersMeta.actions).length})</p>
                <div className="space-y-1">
                  {Object.entries(triggersMeta.actions).map(([key, val]) => (
                    <div key={key} className="bg-blue-50 rounded-lg px-2.5 py-1.5">
                      <span className="text-xs font-medium text-blue-700">{val.label}</span>
                      {val.params.length > 0 && <span className="text-[10px] text-blue-400 ml-1">({val.params.join(', ')})</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {result && (
            <div data-testid="automation-result" className={`rounded-lg p-4 text-sm ${
              result.status === 'created' ? 'bg-green-50 border border-green-200 text-green-800' :
              'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {result.status === 'created' ? (
                <div>
                  <p className="font-semibold">Automatizacion creada: {result.automation?.name}</p>
                  <p className="mt-1 text-green-600">{result.automation?.description}</p>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-green-100 rounded text-xs font-medium">
                      Trigger: {TRIGGER_LABELS[result.automation?.trigger?.type] || result.automation?.trigger?.type}
                    </span>
                    {result.automation?.actions?.map((a, i) => (
                      <span key={i} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                        {ACTION_LABELS[a.type] || a.type}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <p>{result.message} {result.suggestion && <span className="block mt-1 text-red-600">{result.suggestion}</span>}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="font-semibold text-gray-900">Mis Automatizaciones</h3>
          <span className="text-xs text-gray-400">{automations.length} regla{automations.length !== 1 ? 's' : ''}</span>
        </div>

        {automations.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            No tienes automatizaciones. Crea una arriba con lenguaje natural.
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {automations.map(auto => (
              <div key={auto.id} data-testid={`automation-${auto.id}`} className="px-6 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${auto.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{auto.name}</h4>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{auto.description}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded font-medium">
                        {TRIGGER_LABELS[auto.trigger?.type] || auto.trigger?.type}
                      </span>
                      {auto.actions?.map((a, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-medium">
                          {ACTION_LABELS[a.type] || a.type}
                        </span>
                      ))}
                      <span className="text-xs text-gray-400">{auto.executions} ejecucion{auto.executions !== 1 ? 'es' : ''}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => fetchHistory(auto.id)} data-testid={`history-${auto.id}`}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 transition-colors" title="Historial">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    <button onClick={() => toggleAutomation(auto.id)} data-testid={`toggle-${auto.id}`}
                      className={`p-1.5 transition-colors ${auto.active ? 'text-green-500 hover:text-red-500' : 'text-gray-400 hover:text-green-500'}`}
                      title={auto.active ? 'Desactivar' : 'Activar'}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        {auto.active ? (
                          <path d="M6 4h4v16H6zm8 0h4v16h-4z" />
                        ) : (
                          <path d="M8 5v14l11-7z" />
                        )}
                      </svg>
                    </button>
                    <button onClick={() => deleteAutomation(auto.id)} data-testid={`delete-${auto.id}`}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors" title="Eliminar">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {expandedId === auto.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-2">Historial de ejecuciones</p>
                    {(history[auto.id] || []).length === 0 ? (
                      <p className="text-xs text-gray-400">Sin ejecuciones aun</p>
                    ) : (
                      <div className="space-y-1">
                        {history[auto.id].map((h, i) => (
                          <div key={i} className="text-xs text-gray-500 flex justify-between">
                            <span>{new Date(h.executed_at).toLocaleString('es-VE')}</span>
                            <span className="text-gray-400">{JSON.stringify(h.trigger_data).slice(0, 60)}...</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationsComponent;
